/**
 * Templates (precedents) React Query hooks — aligned with FE spec v1.1.
 *
 * Notable shape changes from v1.0:
 *   - No `useTemplateDetail` — the list-row entry IS the detail (the FE
 *     reads from the cached active or archived list).
 *   - Every lifecycle mutation resolves to a `DocumentTemplateEntry` (no
 *     `null` for the prior 204 no-ops).
 */

'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UseMutationOptions,
  UseQueryOptions
} from '@tanstack/react-query';
import { createMutationHook } from '../mutation-utils';
import { templateKeys } from '@/lib/query-keys';
import {
  archiveTemplate,
  commitTemplate,
  editInWord,
  fetchActiveTemplates,
  fetchArchivedTemplates,
  fetchMergeFieldsRegistry,
  fetchTemplateHistory,
  fetchTemplateVersions,
  previewUploadedTemplate,
  renameTemplate,
  replaceTemplateContent,
  restoreTemplateVersion,
  setTemplateDefault,
  unarchiveTemplate
} from './client';
import type {
  ArchiveTemplateRequest,
  CommitTemplateRequest,
  DocumentTemplateEntry,
  DocumentTemplateHistoryEvent,
  DocumentTemplateVersion,
  DocumentType,
  EditInWordRequest,
  EditInWordResponse,
  MergeFieldsRegistry,
  ReplaceContentRequest,
  UploadPreviewResponse
} from './types';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useTemplatesList(
  options?: Omit<
    UseQueryOptions<DocumentTemplateEntry[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: templateKeys.list(),
    queryFn: () => fetchActiveTemplates(),
    staleTime: 30_000,
    ...options
  });
}

export function useArchivedTemplatesList(
  enabled = true,
  options?: Omit<
    UseQueryOptions<DocumentTemplateEntry[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: templateKeys.archived(),
    queryFn: () => fetchArchivedTemplates(),
    enabled,
    staleTime: 30_000,
    ...options
  });
}

export function useTemplateVersions(
  id: number | null,
  options?: Omit<
    UseQueryOptions<DocumentTemplateVersion[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: templateKeys.versions(id ?? 0),
    queryFn: () => fetchTemplateVersions(id as number),
    enabled: id != null,
    ...options
  });
}

export function useTemplateHistory(
  id: number | null,
  options?: Omit<
    UseQueryOptions<DocumentTemplateHistoryEvent[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: templateKeys.history(id ?? 0),
    queryFn: () => fetchTemplateHistory(id as number),
    enabled: id != null,
    ...options
  });
}

export function useMergeFieldsRegistry(
  documentType: DocumentType | null,
  options?: Omit<UseQueryOptions<MergeFieldsRegistry>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: templateKeys.mergeFields(
      documentType ?? ('declaration' as DocumentType)
    ),
    queryFn: () => fetchMergeFieldsRegistry(documentType as DocumentType),
    enabled: documentType != null,
    staleTime: 5 * 60_000,
    ...options
  });
}

// ---------------------------------------------------------------------------
// Upload preview (step 2)
// ---------------------------------------------------------------------------

export function useUploadPreview() {
  return useMutation<
    UploadPreviewResponse,
    Error,
    { fileId: number; documentType: DocumentType; officeId?: number }
  >({
    mutationFn: ({ fileId, documentType, officeId }) =>
      previewUploadedTemplate(fileId, documentType, officeId)
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCommitTemplate(
  options?: UseMutationOptions<
    DocumentTemplateEntry,
    Error,
    CommitTemplateRequest
  >
) {
  const useHook = createMutationHook<
    DocumentTemplateEntry,
    CommitTemplateRequest
  >({
    mutationFn: commitTemplate,
    invalidateKeys: [templateKeys.all],
    onSuccessMessage: (data) => `Saved ${data.name}`,
    onErrorMessage: 'Failed to save template'
  });
  return useHook(options);
}

export function useRenameTemplate(
  options?: UseMutationOptions<
    DocumentTemplateEntry,
    Error,
    { id: number; name: string }
  >
) {
  const useHook = createMutationHook<
    DocumentTemplateEntry,
    { id: number; name: string }
  >({
    mutationFn: ({ id, name }) => renameTemplate(id, name),
    invalidateKeys: ({ id }) => [templateKeys.list(), templateKeys.history(id)],
    onSuccessMessage: () => 'Template renamed',
    onErrorMessage: 'Failed to rename template'
  });
  return useHook(options);
}

export function useReplaceContent(
  options?: UseMutationOptions<
    DocumentTemplateEntry,
    Error,
    { id: number; data: ReplaceContentRequest }
  >
) {
  const useHook = createMutationHook<
    DocumentTemplateEntry,
    { id: number; data: ReplaceContentRequest }
  >({
    mutationFn: ({ id, data }) => replaceTemplateContent(id, data),
    invalidateKeys: ({ id }) => [
      templateKeys.list(),
      templateKeys.versions(id)
    ],
    onSuccessMessage: (data) => `Updated ${data.name}`,
    onErrorMessage: 'Failed to replace content'
  });
  return useHook(options);
}

export function useSetTemplateDefault(
  options?: UseMutationOptions<DocumentTemplateEntry, Error, { id: number }>
) {
  const queryClient = useQueryClient();
  const useHook = createMutationHook<DocumentTemplateEntry, { id: number }>({
    mutationFn: ({ id }) => setTemplateDefault(id),
    // Set-default flips `isDefault` on two rows AND writes history on both,
    // so we wipe the whole templates namespace to keep cache honest.
    invalidateKeys: [templateKeys.all],
    onSuccessMessage: () => 'Set as default',
    onErrorMessage: 'Failed to set as default'
  });
  return useHook({
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: templateKeys.all });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    }
  });
}

export function useArchiveTemplate(
  options?: UseMutationOptions<
    DocumentTemplateEntry,
    Error,
    { id: number; data?: ArchiveTemplateRequest }
  >
) {
  const useHook = createMutationHook<
    DocumentTemplateEntry,
    { id: number; data?: ArchiveTemplateRequest }
  >({
    mutationFn: ({ id, data }) => archiveTemplate(id, data),
    invalidateKeys: ({ id }) => [
      templateKeys.list(),
      templateKeys.archived(),
      templateKeys.history(id)
    ],
    onSuccessMessage: () => 'Template archived',
    onErrorMessage: 'Failed to archive template'
  });
  return useHook(options);
}

export function useUnarchiveTemplate(
  options?: UseMutationOptions<DocumentTemplateEntry, Error, { id: number }>
) {
  const useHook = createMutationHook<DocumentTemplateEntry, { id: number }>({
    mutationFn: ({ id }) => unarchiveTemplate(id),
    invalidateKeys: ({ id }) => [
      templateKeys.list(),
      templateKeys.archived(),
      templateKeys.history(id)
    ],
    onSuccessMessage: () => 'Template unarchived',
    onErrorMessage: 'Failed to unarchive template'
  });
  return useHook(options);
}

export function useRestoreVersion(
  options?: UseMutationOptions<
    DocumentTemplateEntry,
    Error,
    { id: number; versionId: number }
  >
) {
  const useHook = createMutationHook<
    DocumentTemplateEntry,
    { id: number; versionId: number }
  >({
    mutationFn: ({ id, versionId }) => restoreTemplateVersion(id, versionId),
    invalidateKeys: ({ id }) => [
      templateKeys.list(),
      templateKeys.versions(id),
      templateKeys.history(id)
    ],
    onSuccessMessage: () => 'Version restored',
    onErrorMessage: 'Failed to restore version'
  });
  return useHook(options);
}

// ---------------------------------------------------------------------------
// Edit in Word (provision-on-edit) — LF-5224 / BE-11
// ---------------------------------------------------------------------------

/**
 * Provision-on-edit mutation. On success the whole templates namespace is
 * invalidated — the target row flips to `checked_out`, and a fork-first
 * request also mints a brand-new firm row. The caller opens the returned
 * `webUrl` in a new tab and handles coded errors (soft-lock, provisioning
 * failure) itself, so success/error toasts are left to the component rather
 * than baked in here.
 */
export function useEditInWord(
  options?: UseMutationOptions<EditInWordResponse, Error, EditInWordRequest>
) {
  const queryClient = useQueryClient();
  return useMutation<EditInWordResponse, Error, EditInWordRequest>({
    mutationFn: editInWord,
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: templateKeys.all });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    }
  });
}

export { templateKeys };
